import { useState } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { Sun, History, Settings, LogOut, BookOpenCheck, MessageCircleQuestion, Shield, GraduationCap, Users, CalendarRange, Menu, NotebookPen, Target, MessagesSquare, Mail, BookHeart } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AnnouncementBanner } from "@/components/Announcements";
import { ActingBanner } from "@/components/ActAs";
import { useEdition } from "@/lib/edition";

const MAIN = [
  { to: "/", label: "Brief", icon: Sun, id: "nav-brief" },
  { to: "/homework", label: "Homework", c: "Tasks", icon: BookOpenCheck, id: "nav-homework" },
  { to: "/revise", label: "Revise", c: "Learning", icon: GraduationCap, id: "nav-revise" },
  { to: "/groups", label: "Groups", c: "Teams", icon: Users, id: "nav-groups" },
  { to: "/messages", label: "Messages", icon: MessagesSquare, id: "nav-messages" },
  { to: "/ask", label: "Ask Daybreyk", icon: MessageCircleQuestion, id: "nav-ask" },
];
const MORE = [
  { to: "/daylog", label: "Day log", c: "Work log", icon: BookHeart, id: "more-daylog" },
  { to: "/week", label: "Week view", icon: CalendarRange, id: "more-week" },
  { to: "/notes", label: "Lesson notes", c: "Meeting notes", icon: NotebookPen, id: "more-notes" },
  { to: "/plan", label: "Targets & exams", icon: Target, id: "more-targets", studentOnly: true },
  { to: "/history", label: "History", icon: History, id: "more-history" },
  { to: "/connections", label: "Settings", icon: Settings, id: "more-settings" },
  { to: "/contact", label: "Contact us", icon: Mail, id: "more-contact" },
];

const Item = ({ to, label, c, icon: I, id, close }) => {
  const { w } = useEdition();
  return (
    <NavLink to={to} end={to === "/"} onClick={close} data-testid={id}
      className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-4 py-3 text-[15px] font-semibold transition-colors ${isActive ? "bg-gold-400 text-black shadow-gold" : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}>
      <I className="w-5 h-5 shrink-0" />{w(label, c ?? label)}
    </NavLink>
  );
};

const SideMenu = ({ open, setOpen, isAdmin, logout }) => {
  const close = () => setOpen(false);
  const { corp } = useEdition();
  const more = [...MORE.filter((m) => !(corp && m.studentOnly)), ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: Shield, id: "more-admin" }] : [])];
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="left" data-testid="side-menu" className="w-[82%] max-w-xs sm:max-w-xs overflow-y-auto border-r border-slate-200 bg-white p-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader className="px-2">
          <SheetTitle className="flex items-center gap-2.5 font-display text-2xl text-slate-900 text-left"><Logo className="w-8 h-8" />Daybreyk</SheetTitle>
        </SheetHeader>
        <nav className="mt-5 space-y-1">{MAIN.map((m) => <Item key={m.to} {...m} close={close} />)}</nav>
        <p className="mt-5 mb-1 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">More</p>
        <nav className="space-y-1">{more.map((m) => <Item key={m.to} {...m} close={close} />)}</nav>
        <button data-testid="menu-logout" onClick={logout} className="mt-4 flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-[15px] font-semibold text-rose-700 hover:bg-rose-50">
          <LogOut className="w-5 h-5" />Sign out
        </button>
      </SheetContent>
    </Sheet>
  );
};

const TopBar = ({ onMenu }) => {
  const { pathname } = useLocation();
  const { w } = useEdition();
  const page = [...MAIN, ...MORE, { to: "/admin", label: "Admin" }].find((m) => (m.to === "/" ? pathname === "/" : pathname.startsWith(m.to)));
  return (
    <header className="sticky top-0 z-40 pt-[env(safe-area-inset-top)] glass !border-x-0 !border-t-0">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="h-14 lg:h-16 flex items-center gap-3">
          <button data-testid="nav-menu" onClick={onMenu} aria-label="Open menu" className="inline-flex items-center gap-2 h-10 rounded-full border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-800 transition-colors hover:border-gold-400">
            <Menu className="w-5 h-5" /><span className="hidden sm:inline">Menu</span>
          </button>
          <NavLink to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
            <Logo className="w-9 h-9" />
            <span className="font-display text-2xl text-slate-900">Daybreyk</span>
          </NavLink>
          {page && <span data-testid="nav-current-page" className="ml-auto truncate text-sm font-semibold text-slate-600">{w(page.label, page.c ?? page.label)}</span>}
        </div>
      </div>
    </header>
  );
};

export const AppShell = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [more, setMore] = useState(false);
  if (user === null) return <div className="min-h-screen grid place-items-center font-mono text-xs uppercase tracking-widest text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.conduct_ok === false && pathname !== "/conduct") return <Navigate to="/conduct" replace />;
  if (user.conduct_ok === false) return <Outlet />;
  if (user.onboarded === false && pathname !== "/welcome") return <Navigate to="/welcome" replace />;

  return (
    <div className="min-h-screen pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <TopBar onMenu={() => setMore(true)} />
      <InstallPrompt />
      <ActingBanner />
      <AnnouncementBanner />
      <Outlet />
      <SideMenu open={more} setOpen={setMore} isAdmin={Boolean(user.is_admin)} logout={logout} />
    </div>
  );
};
